import { detectLocale } from "@/app/i18n/detect";

/** Russian time zones — a device here reaches u-memo.ru reliably while u-memo.com may be blocked. */
const RUSSIAN_TIME_ZONES =
  /^(Europe\/(Moscow|Kaliningrad|Samara|Volgograd|Simferopol|Kirov|Saratov|Astrakhan|Ulyanovsk)|Asia\/(Yekaterinburg|Omsk|Novosibirsk|Barnaul|Tomsk|Novokuznetsk|Krasnoyarsk|Irkutsk|Chita|Yakutsk|Khandyga|Vladivostok|Ust-Nera|Magadan|Sakhalin|Srednekolymsk|Kamchatka|Anadyr))$/;

/** True when the device looks Russian by time zone, locale region, or language. */
export function prefersRussianBackend(
  timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone || "",
  language: string = globalThis.navigator?.language ?? "",
): boolean {
  if (RUSSIAN_TIME_ZONES.test(timeZone)) return true;
  const region = language.split(/[-_]/).find((part, i) => i > 0 && part.length === 2)?.toUpperCase();
  if (region === "RU") return true;
  return detectLocale(language) === "ru";
}

/**
 * Where the app's `/api` calls go. The web build serves the API on the same
 * origin (`/api`); a native build has no same-origin backend, so it targets the
 * regional deployment — the Russian copy for Russian devices, the .com copy
 * otherwise. `VITE_API_BASE` overrides everything (staging, tests).
 */
export function resolveApiBase(): string {
  const override = import.meta.env.VITE_API_BASE;
  if (override) return override;
  if (isNativeBuild()) return `${nativeSiteOrigin()}/api`;
  return "/api";
}

/**
 * A page of the website. The web build links it relatively; the native bundle has no such page
 * to open in a new window (capacitor:// has no handler), so it links the regional site, which
 * iOS opens in Safari.
 */
export function sitePageUrl(path: string): string {
  return isNativeBuild() ? `${nativeSiteOrigin()}${path}` : path;
}

function nativeSiteOrigin(): string {
  return prefersRussianBackend() ? "https://u-memo.ru" : "https://u-memo.com";
}

/** True in the native (Capacitor) build; the PWA service worker is off there. */
export function isNativeBuild(): boolean {
  return import.meta.env.VITE_TARGET === "native";
}
